'use client';

import {
  ASK_IMAGE_TYPES,
  type AskImage,
  type AskImageType
} from '@shared/all/domains/electron';

// Images for an AI question: taken off the clipboard or a drop, shrunk to what
// a model can use, and handed over as base64. Nothing here uploads.

/** Every image file in a paste or a drop. `clipboardData` and `dataTransfer`
 *  are the same type, so one reader serves both. */
export function imageFilesFrom(transfer: DataTransfer | null): File[] {
  if (!transfer) return [];
  const files: File[] = [];
  for (let i = 0; i < transfer.items.length; i++) {
    const item = transfer.items[i];
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      const file = item.getAsFile();
      if (file) files.push(file);
    }
  }
  return files;
}

const isAskImageType = (type: string): type is AskImageType =>
  (ASK_IMAGE_TYPES as readonly string[]).includes(type);

// A model downsizes anything past this edge itself, so more pixels only cost
// transfer time on the link to the runner.
const MAX_EDGE = 1568;
// A file this small and already within MAX_EDGE goes as it is.
const PASS_THROUGH_BYTES = 1_000_000;
const JPEG_QUALITY = 0.9;

const readBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.slice(dataUrl.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error('Read failed'));
    reader.readAsDataURL(blob);
  });

/**
 * One file as an image a model can read. A small PNG, JPEG or WEBP passes
 * through untouched. Anything larger, and every GIF, is redrawn on a canvas:
 * that caps the size and leaves a GIF as its first frame, a still.
 */
export async function fileToAskImage(file: File): Promise<AskImage> {
  if (!isAskImageType(file.type)) {
    throw new Error(
      `${file.name || 'That file'} is not a PNG, JPEG, WEBP or GIF.`
    );
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('Image decode failed'));
      i.src = url;
    });
    const scale = Math.min(
      1,
      MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight)
    );
    if (
      scale === 1 &&
      file.type !== 'image/gif' &&
      file.size <= PASS_THROUGH_BYTES
    ) {
      return { mediaType: file.type, data: await readBase64(file) };
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas context unavailable');
    // JPEG has no alpha: without a backdrop, transparent pixels turn black.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('toBlob failed'))),
        'image/jpeg',
        JPEG_QUALITY
      )
    );
    return { mediaType: 'image/jpeg', data: await readBase64(blob) };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Every readable image among `files`, plus the first reason one was not. */
export async function filesToAskImages(
  files: File[]
): Promise<{ images: AskImage[]; error: string }> {
  const results = await Promise.allSettled(files.map(fileToAskImage));
  const images: AskImage[] = [];
  let error = '';
  for (const result of results) {
    if (result.status === 'fulfilled') images.push(result.value);
    else if (!error) {
      error =
        result.reason instanceof Error
          ? result.reason.message
          : 'Could not read that image.';
    }
  }
  return { images, error };
}
